import React, { Component } from "react";
import Tree from "react-d3-tree";
import { withRouter } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ThemeProvider } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import FitScreenRoundedIcon from "@mui/icons-material/FitScreenRounded";
import FullscreenExitRoundedIcon from "@mui/icons-material/FullscreenExitRounded";
import FullscreenRoundedIcon from "@mui/icons-material/FullscreenRounded";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import SchemaOutlinedIcon from "@mui/icons-material/SchemaOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import NavigateNextRoundedIcon from "@mui/icons-material/NavigateNextRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import ZoomInRoundedIcon from "@mui/icons-material/ZoomInRounded";
import ZoomOutRoundedIcon from "@mui/icons-material/ZoomOutRounded";
import "./styles.css";
import "../auth/auth.css";
import HoverComponent from "./HoverComponent";
import { withoutWayBack } from "./withoutWayBack";
import DetailsPanel from "../app/DetailsPanel";
import { EMPTY_DETAILS, loadObjectDetails } from "../app/objectDetails";
import { appendNodes, popNodes, quickCheckAccess, sortByProperty } from "../../utils/accessMapFunctions";
import { uuidv4 } from "../../utils/utilities";
import {
  getDiagramAvailability,
  getEdges,
  getNodes,
  getProcessAttributesByPartialName,
  getExplorerStart,
} from "../../actions";
import businessFlowsTheme, { brand, radius } from "../../theme/businessFlowsTheme";
import ExplorerLegend from "./ExplorerLegend";
import TypeIcon from "../app/typeIcons";
import { useViewConfig } from "../app/ViewConfigContext";
import { formatLabel, relationshipLabel, setting, shortName, typeInfo } from "../../utils/viewConfig";
import { carriesNote, connectionSentence, directionMark, groupTitle } from "./relationshipGroups";
import RelationshipDetails from "./RelationshipDetails";
import { distinguishers } from "./distinguish";
import { sortFormats } from "../diagram/formatRoute";
import { isDatabaseUnreachable, unreachableMessage } from "../../utils/databaseOffline";
import { LOCKED_LABEL, LOCKED_NOTICE } from "../../features";

const NODE_WIDTH = 240;
const NODE_HEIGHT = 52;
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 1.5;
const FOCUS_ZOOM = 0.9;
// Nodes glide into place; focus/fit read positions only after that finishes.
const TRANSITION_MS = 250;
const SETTLE_MS = TRANSITION_MS + 80;
// How many relationship groups the first level opens with. An object with more
// than this would fill the canvas with pills before the reader has chosen
// anything; the rest are one click away and the canvas says so.
const FIRST_LEVEL_CAP = 12;

// Path of nodes from the root to the node with this id (inclusive), or [].
// An object under a relationship, as the panel reads it - the same shape
// whether it came off the canvas or from the panel's own read.
const asGroupItem = (child) => ({
  guid: child.guid,
  name: child.fullName,
  type: child.type,
  attributes: child._relationshipAttributes || [],
  // The object's own properties, so the panel can find what tells two
  // objects shown under the same name apart (./distinguish.js).
  item: child.item || null,
});

const findPath = (node, id, trail = []) => {
  if (!node) return [];
  const next = [...trail, node];
  if (node.id === id) return next;
  for (const child of node.children || []) {
    const found = findPath(child, id, next);
    if (found.length) return found;
  }
  return [];
};



function requestFullscreen(elem) {
  if (!elem) return;
  try {
    (elem.requestFullscreen || elem.webkitRequestFullscreen || elem.msRequestFullscreen)?.call(elem);
  } catch (err) {
    console.error(err);
  }
}

function exitFullscreen() {
  try {
    (document.exitFullscreen || document.webkitExitFullscreen || document.msExitFullscreen)?.call(document);
  } catch (err) {
    console.error(err);
  }
}

function ToolButton({ title, children, ...rest }) {
  return (
    <Tooltip title={title}>
      <span>
        <IconButton size="small" aria-label={title} {...rest} sx={{ borderRadius: radius.control, color: brand.ink, ...rest.sx }}>
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}

// The object types on screen, for the legend.
const typesInTree = (node, found = new Set()) => {
  if (!node) return found;
  if (!node.isLink && node.type) found.add(node.type);
  (node.children || []).forEach((child) => typesInTree(child, found));
  return found;
};

var factSheetShowTimeout, hoverTimeOut;

class CustomD3Tree extends Component {
  // The nodes being opened right now, so two callers never open one twice.
  opening = new Map();

  constructor(props) {
    super(props);
    // The URL names the object to explore. A node handed over from search or a
    // diagram is used only when it is that same object; otherwise the tree
    // waits for it rather than showing the last thing explored and correcting
    // itself a moment later.
    const urlGuid = props.location?.pathname?.split("/").pop();
    const handedOver = props.nodeDatum?.guid && props.nodeDatum.guid === urlGuid ? props.nodeDatum : null;
    this.state = {
      data: handedOver,
      nodeDatum: { id: "", guid: "" },
      show: false,
      dimensions: undefined,
      translate: { x: 80, y: 300 },
      nodeSize: { x: 330, y: 62 },
      isFullScreen: false,
      zoom: 0.9,
      selected: null,
      // The relationship the reader last clicked. While it is set the panel
      // reads that relationship instead of an object - clicking a relationship
      // is how an edge is read here, there being no line to click in a tree.
      selectedGroupId: null,
      // What the chosen relationship reaches, read for the panel: `{ id,
      // items }`, with `items` null while it is being read.
      groupItems: null,
      // An object the relationship panel was asked to point at. It wears the
      // ring like a selected one, but it is not what the details panel is
      // showing - that stays whatever the reader last asked for.
      highlightId: null,
      details: EMPTY_DETAILS,
      panelOpen: true,
      drawerOpen: false,
      focusId: null,
      // How many relationship groups the first level left unopened, if any.
      firstLevelHidden: 0,
      // Set when the database itself can't be reached: the server's own words
      // (which database, why, who fixes it). Shown instead of an empty canvas.
      databaseError: null,
    };
    this.onFullscreenChange = () => this.setState({ isFullScreen: Boolean(document.fullscreenElement) });
    // Escape puts the canvas back the way it was: the fact sheet that follows
    // the pointer goes away, and so does full screen (the browser's own doing).
    this.onEscape = (event) => {
      if (event.key !== "Escape") return;
      clearTimeout(factSheetShowTimeout);
      clearTimeout(hoverTimeOut);
      if (this.state.show) this.setState({ show: false });
    };
  }

  // One object as the tree's root card, read from the database: its
  // attributes, and whether a diagram of it exists.
  async loadObject(guid) {
    const [response, availability] = await Promise.all([
      getProcessAttributesByPartialName(guid),
      getDiagramAvailability([guid]),
    ]);
    // Nothing could be asked at all: say so rather than drawing a nameless card.
    if (isDatabaseUnreachable(response)) {
      this.setState({ databaseError: unreachableMessage(response) });
      return null;
    }
    this.setState({ databaseError: null });
    // An object opened by its fallback id may have been given a real one on
    // the way (the server answers under it): keep the link people can save
    // pointing at the object, not at a node number. history.replace, so Back
    // still leaves the explorer rather than stepping through ids.
    const canonical = response.guid;
    if (canonical && canonical !== guid) {
      guid = canonical;
      if (this.props.path) this.props.history?.replace(`${this.props.path}/object/${encodeURIComponent(canonical)}`);
    }
    return {
      id: uuidv4(),
      guid,
      shortName: this.getShortName(response.content?.name),
      fullName: response.content?.name,
      type: response.content?.Type,
      description: response.content?.Description,
      children: [],
      isLink: false,
      inTrial: true,
      url: response.content?.URL,
      item: response.content || null,
      hasDiagram: Boolean(availability[guid]),
      // Which formats, not only whether: an affordance is named after the
      // diagram it opens (BPMN, SVG, Mermaid, PDF), never "Diagram".
      diagramFormats: availability[guid] || [],
      // The answer that drew this card, kept so the details panel can open on
      // this object without asking for its attributes a second time.
      attributesResponse: response,
    };
  }

  straightPathFunc = (linkDatum, orientation) => {
    const { source, target } = linkDatum;
    if (orientation !== "horizontal") return `M${source.x},${source.y}L${target.x},${target.y}`;
    // Smooth curve from the right edge of the source card to the left edge of the target.
    const startX = source.y + NODE_WIDTH;
    const midX = (startX + target.y) / 2;
    return `M${startX},${source.x} C${midX},${source.x} ${midX},${target.x} ${target.y},${target.x}`;
  };

  getDynamicPathClass = ({ target }) => (target.children ? "link__to-branch" : "link__to-leaf");

  checkAccessibility(nodeDatum) {
    if (nodeDatum.isLink) return true;
    // The object the explorer was opened on is always reachable.
    if (nodeDatum.guid && nodeDatum.guid === this.state.data?.guid) return true;
    return Boolean(nodeDatum && quickCheckAccess(nodeDatum?.inTrial, this.props.user.subscription));
  }

  // "Start over" returns to where this database starts: the object an admin
  // chose in Views, else its root. Asked for when the button is pressed, so a
  // change to that choice reaches a page that is already open.
  startOver = async () => {
    const start = await getExplorerStart();
    // A database that couldn't be asked is not a database without a start
    // object, and the two need different words.
    if (isDatabaseUnreachable(start)) {
      const msg = unreachableMessage(start);
      this.setState({ databaseError: msg });
      toast.warning(msg);
      return;
    }
    const guid = start.guid;
    if (typeof guid !== "string" || !guid) {
      toast.warning("This database has no starting point yet. An admin can choose one under Views.");
      return;
    }
    const data = await this.loadObject(guid);
    if (!data) return;
    if (this.props.path) this.props.history.replace(`${this.props.path}/object/${guid}`);
    this._firstLevelOpened = false;
    this.setState(
      { data, zoom: FOCUS_ZOOM, focusId: null, selected: null, details: EMPTY_DETAILS, show: false, firstLevelHidden: 0 },
      () => this.openOnStartObject()
    );
  };

  // What the explorer does the moment it has the object it was opened on:
  // the panel fills with that object, its first level of relationships is
  // opened, and the result is fitted to the canvas. Without this the canvas
  // read as an empty page with one small card in it, and a quarter of the
  // screen said "No result selected".
  openOnStartObject() {
    this.centralizeNodes();
    this.showStartDetails();
    this.openFirstLevel();
  }

  // The object the explorer opened on is what the panel shows until the
  // reader picks another - and it is marked as the selected card on the
  // canvas, so what is shown is never a mystery. Its attributes were read to
  // draw the card, so only its occurrences and relationships are asked for.
  showStartDetails() {
    const node = this.state.data;
    if (!node?.guid || this.state.selected) return;
    this.showDetails(node, { reveal: false, known: node.attributesResponse });
  }

  // The first level, by the very path a click takes: the relationship groups
  // around the start object, from the same query, under the same view rules.
  openFirstLevel = async () => {
    const root = this.state.data;
    if (this._firstLevelOpened || !root?.guid || root.children?.length) return;
    this._firstLevelOpened = true;
    await this.addChildNode(root);
    this.setState(
      (state) => {
        const groups = state.data?.children || [];
        if (groups.length <= FIRST_LEVEL_CAP) return { focusId: state.data?.id || null };
        return {
          data: { ...state.data, children: groups.slice(0, FIRST_LEVEL_CAP) },
          firstLevelHidden: groups.length - FIRST_LEVEL_CAP,
          focusId: state.data.id,
        };
      },
      () => this.scheduleFit(this.state.data?.id)
    );
  };

  centralizeNodes() {
    if (this.treeContainer) {
      const dimensions = this.treeContainer.getBoundingClientRect();
      const isFirst = this.state.dimensions === undefined;
      this.setState({
        dimensions: { width: dimensions.width, height: dimensions.height },
        ...(isFirst && {
          translate: { x: Math.max(32, Math.min(200, dimensions.width / 8)), y: dimensions.height / 2 },
        }),
      });
    }
  }

  // Scale and position the tree so it fits the canvas. When it can't fit at a
  // readable size, keep the newest (right-most) column and the focused node in view.
  fitView = (focusNodeId) => {
    const container = this.treeContainer;
    const g = container?.querySelector(".rd3t-g");
    if (!g || typeof g.getBBox !== "function") return;
    const box = g.getBBox();
    if (!box.width || !box.height) return;
    const rect = container.getBoundingClientRect();
    const pad = 32;
    // The note about the first level's cap sits in the top right corner: the
    // tree is fitted below it rather than under it.
    const topReserve = this.state.firstLevelHidden > 0 ? 56 : 0;
    const availW = Math.max(1, rect.width - pad * 2);
    const availH = Math.max(1, rect.height - pad * 2 - 44 - topReserve); // room for the legend bar
    const zoom = Math.min(1, Math.max(0.35, Math.min(availW / box.width, availH / box.height)));

    const contentW = box.width * zoom;
    const contentH = box.height * zoom;
    let x = contentW <= availW
      ? pad + (availW - contentW) / 2 - box.x * zoom
      : Math.min(pad - box.x * zoom, rect.width - pad - (box.x + box.width) * zoom);
    let y = pad + topReserve + (availH - contentH) / 2 - box.y * zoom;

    if (contentH > availH && focusNodeId) {
      const focus = container.querySelector(`[id="${focusNodeId}"]`);
      const match = focus?.getAttribute("transform")?.match(/translate\(([-\d.]+),\s*([-\d.]+)\)/);
      if (match) y = rect.height / 2 - parseFloat(match[2]) * zoom;
    }
    this.applyView(x, y, zoom);
  };

  // react-d3-tree can re-apply a stale d3 transform after prop changes, so set the
  // view on the SVG directly as well (and on d3's zoom state, so dragging continues from here).
  applyView = (x, y, zoom) => {
    const k = +zoom.toFixed(3);
    this.setState({ zoom: k, translate: { x, y } }, () => {
      requestAnimationFrame(() => {
        const container = this.treeContainer;
        const svg = container?.querySelector("svg");
        const g = container?.querySelector(".rd3t-g");
        if (svg?.__zoom?.constructor) svg.__zoom = new svg.__zoom.constructor(k, x, y);
        g?.setAttribute("transform", `translate(${x},${y}) scale(${k})`);
      });
    });
  };

  // Keep cards readable and bring the clicked node (and its children) into view.
  focusOn = (nodeId, expanded = true) => {
    const container = this.treeContainer;
    if (!container || !nodeId) return;
    const anchor = container.querySelector(`[data-node-id="${nodeId}"]`);
    const g = anchor?.closest("g[transform]");
    const match = g?.getAttribute("transform")?.match(/translate\(([-\d.]+),\s*([-\d.]+)\)/);
    if (!match) return;
    const nodeX = parseFloat(match[1]);
    const nodeY = parseFloat(match[2]);
    const rect = container.getBoundingClientRect();
    const compact = rect.width < 700;
    // Narrow screens need a smaller scale so a node and its children column both fit.
    const zoom = compact
      ? Math.min(0.6, Math.max(0.4, rect.width / ((NODE_WIDTH + this.state.nodeSize.x) * 1.15)))
      : this.state.zoom >= 0.7 && this.state.zoom <= 1.2 ? this.state.zoom : FOCUS_ZOOM;
    // Expanded: the node sits left of centre so its children column fits beside it.
    const anchorX = expanded
      ? compact ? 12 : Math.max(24, rect.width / 2 - (NODE_WIDTH + this.state.nodeSize.x) * zoom / 2)
      : rect.width / 2 - (NODE_WIDTH * zoom) / 2;
    this.applyView(this.keepInView(anchorX - nodeX * zoom, zoom, rect), (rect.height - 44) / 2 - nodeY * zoom, zoom);
  };

  // Drilling in moves the view right to make room for the new column, which
  // used to push the card the reader started from half off the left edge. Where
  // the whole tree fits the canvas it is held inside it; where it doesn't, the
  // newest column still wins - but there is never blank space on the left while
  // cards hang off the right.
  keepInView = (x, zoom, rect) => {
    const g = this.treeContainer?.querySelector(".rd3t-g");
    const box = typeof g?.getBBox === "function" ? g.getBBox() : null;
    if (!box?.width) return x;
    const pad = 24;
    const atLeftEdge = pad - box.x * zoom;
    const atRightEdge = rect.width - pad - (box.x + box.width) * zoom;
    const fits = box.width * zoom <= rect.width - pad * 2;
    return fits ? Math.min(Math.max(x, atLeftEdge), atRightEdge) : Math.min(x, atLeftEdge);
  };

  scheduleFocus = (nodeId, expanded) => {
    clearTimeout(this.fitTimer);
    this.fitTimer = setTimeout(() => this.focusOn(nodeId, expanded), SETTLE_MS);
  };

  // Drill back out to a node on the breadcrumb path.
  drillOutTo = (index) => {
    const path = findPath(this.state.data, this.state.focusId);
    const target = path[index];
    if (!target) return;
    const nextData = { ...this.state.data };
    const deeper = path[index + 1];
    if (deeper) popNodes(deeper.id, nextData);
    this.setState({ data: nextData, focusId: target.id }, () => this.scheduleFocus(target.id, true));
  };

  scheduleFit = (focusNodeId) => {
    clearTimeout(this.fitTimer);
    this.fitTimer = setTimeout(() => this.fitView(focusNodeId), SETTLE_MS);
  };

  async componentDidMount() {
    document.addEventListener("fullscreenchange", this.onFullscreenChange);
    document.addEventListener("keydown", this.onEscape);
    this.centralizeNodes();
    // Refit when the canvas changes size: window resize, panel toggled, full screen.
    if (typeof ResizeObserver !== "undefined" && this.treeContainer) {
      this.resizeObserver = new ResizeObserver(() => {
        this.centralizeNodes();
        if (this.state.focusId) this.scheduleFocus(this.state.focusId, true);
      });
      this.resizeObserver.observe(this.treeContainer);
    }
    const GUID = this.props.location.pathname.split("/").pop();
    const data = await this.loadObject(GUID);
    if (!data) return; // the database couldn't be reached - databaseError says so
    this.setState({ data: { ...data, id: this.state.data?.id || data.id } }, () => this.openOnStartObject());
  }

  componentDidUpdate(prevProps) {
    // The layout can change width (panel toggled, window resized).
    if (prevProps.wide !== this.props.wide) this.centralizeNodes();
  }

  componentWillUnmount() {
    document.removeEventListener("fullscreenchange", this.onFullscreenChange);
    document.removeEventListener("keydown", this.onEscape);
    clearTimeout(factSheetShowTimeout);
    clearTimeout(hoverTimeOut);
    clearTimeout(this.fitTimer);
    this.resizeObserver?.disconnect();
  }

  handleShow(nodeDatum) {
    clearTimeout(hoverTimeOut);
    this.setState({ nodeDatum, show: true });
  }

  handleClose() {
    if (this.state.show) {
      hoverTimeOut = setTimeout(() => this.setState({ show: false }), 300);
    }
  }

  // Show one of the objects a relationship reaches, **on the canvas**, rather
  // than navigating onto it (product owner's reviewer, 7 Oct 2026: "make the
  // list clickable, so I can highlight or expand the related object - I have
  // two relations, one with confidence 1.0 and one with 0.9, how do I open the
  // one with 0.9?").
  //
  // The panel is the only place those values are side by side, so it has to be
  // the place the reader acts on them. Clicking a row used to reload the whole
  // explorer with that object as its centre, which answers a different
  // question - "show me this object's own relationships" - and loses the
  // comparison that prompted it.
  //
  // So: open the relationship where it is closed, then select the object and
  // bring it into view. The object's own card offers the way in, as it always
  // has, for a reader who does want to travel.
  revealObject = async (group, item) => {
    if (!group || !item?.guid) return;
    if (!group.children?.length) await this.addChildNode(group);
    // Read the tree again: `group` is the node as it was before the children
    // arrived.
    const path = findPath(this.state.data, group.id);
    const opened = path[path.length - 1];
    const child = (opened?.children || []).find((one) => one.guid === item.guid);
    if (!child) return;
    // Marked and brought into view, with the relationship panel left open: the
    // reader is comparing two edges, and closing the list they are comparing
    // in would undo the very thing they clicked for.
    //
    // `highlightId` rather than `selected`, which is what the **details panel**
    // is showing. Writing the object into `selected` here put its name over
    // whatever attributes were already loaded - a panel saying one thing about
    // another object, which is worse than saying nothing. The card's own
    // button is still how a reader asks for its details.
    this.setState({ focusId: child.id, highlightId: child.id }, () => this.scheduleFocus(child.id, true));
  };

  // What a relationship reaches, read for the panel rather than taken off the
  // canvas.
  //
  // The canvas holds the objects only while the group is open, so a reader who
  // collapsed one - or who clicked a relationship to read it rather than to
  // walk it - met "Open it on the canvas to see what it reaches" about a
  // relationship whose attributes the panel was already naming. Clicking a
  // thing should tell you about the thing.
  //
  // Kept per group, so clicking the same one again costs nothing, and dropped
  // the moment another is chosen: this is what the reader is looking at, not a
  // store.
  readGroup = async (nodeDatum) => {
    const held = nodeDatum.children?.length ? nodeDatum.children : null;
    if (held) {
      this.setState({ groupItems: { id: nodeDatum.id, items: held.map(asGroupItem) } });
      return;
    }
    this.setState({ groupItems: { id: nodeDatum.id, items: null } });
    const found = await getNodes(nodeDatum.guid, nodeDatum.fullName, nodeDatum.direction);
    // Another relationship was chosen while this one was being read.
    if (this.state.selectedGroupId !== nodeDatum.id) return;
    this.setState({
      groupItems: {
        id: nodeDatum.id,
        items: (found || []).map((node) => ({
          guid: node.GUID,
          name: node.name,
          type: node.Type,
          attributes: node._relationshipAttributes || [],
          item: node,
        })),
      },
    });
  };

  // `reveal` opens the panel (and, on a narrow screen, its drawer): that is
  // what asking for details means. The panel filling itself with the object
  // already on the canvas must not push a drawer over a phone's screen, so it
  // passes false. `known` is an attributes answer already in hand.
  showDetails = async (nodeDatum, { reveal = true, known = null } = {}) => {
    const guid = nodeDatum.guid;
    this.setState({
      selectedGroupId: null,
      groupItems: null,
      highlightId: null,
      selected: {
        GUID: guid,
        name: nodeDatum.fullName,
        Type: nodeDatum.type,
        nodeId: nodeDatum.id,
        // The relationship that put this object on the canvas. The object the
        // reader opened first was reached by no relationship, so it has none.
        connection: connectionSentence(this.props.viewConfig, {
          relationship: nodeDatum._relationship,
          direction: nodeDatum._direction,
          here: nodeDatum._from,
          there: nodeDatum.fullName,
        }),
        // What the relationship that reached it holds, where it holds
        // anything. Shown beside the sentence, never mixed in with the
        // object's own attributes - they are two different things.
        connectionAttributes: nodeDatum._relationshipAttributes || [],
      },
      details: { ...EMPTY_DETAILS, loading: true },
      show: false,
      ...(reveal ? { panelOpen: true, drawerOpen: !this.props.wide } : {}),
    });
    // Always an answer, even for an object that can't be read: the panel says
    // so instead of waiting on its skeletons.
    const loaded = await loadObjectDetails(guid, known);
    if (this.state.selected?.GUID !== guid) return;
    this.setState({
      details: {
        ...loaded,
        occurrences: [...loaded.occurrences].sort(sortByProperty("name")),
        relationships: [...loaded.relationships].sort(sortByProperty("name")),
      },
    });
  };

  findInSearch = (target) => {
    if (!target?.GUID) return;
    const params = new URLSearchParams({ q: target.name || "", guid: target.GUID });
    if (target.Type) params.set("type", target.Type);
    this.props.history.push(`${this.props.path}/advance-search?${params.toString()}`);
  };

  // A diagram node opens the diagram of the object it belongs to.
  // A named format opens that one; without one, the object opens on whatever
  // it would have opened on anyway.
  openDiagram = (nodeDatum, format) => {
    const search = format ? `?format=${encodeURIComponent(format)}` : "";
    window.open(`${this.props.path}/map/${encodeURIComponent(nodeDatum.diagramOf || nodeDatum.guid)}${search}`, "_blank", "noopener");
  };

  openObject = (entry) => {
    const { history } = this.props;
    history.push(`/${this.props.user.mainDatabase}/object/${entry.GUID}`);
    history.go();
  };

  // What this node's diagram is, in this database's words: the mark on a node
  // says "Open the BPMN of X", never "Open the diagram of X", wherever the
  // format is known.
  diagramNameOf = (nodeDatum) => {
    const formats = sortFormats((nodeDatum.diagramFormats || []).filter(Boolean));
    return { format: formats[0] || null, name: formats.length ? formatLabel(this.props.viewConfig, formats[0]) : "diagram" };
  };

  renderNodeWithCustomEvents = ({ nodeDatum, toggleNode }) => {
    const accessible = this.checkAccessibility(nodeDatum);
    const diagram = this.diagramNameOf(nodeDatum);
    // Wearing the ring: the object the details panel is showing, or the one
    // the relationship panel was just asked to point at.
    const selected =
      !nodeDatum.isLink && (this.state.selected?.nodeId === nodeDatum.id || this.state.highlightId === nodeDatum.id);
    const expanded = nodeDatum.children?.length > 0;
    // A group node stands for a relationship (its fullName is the
    // relationship type), an object node for its object type: each reads its
    // own name from the configuration.
    const info = nodeDatum.isLink
      ? { label: relationshipLabel(this.props.viewConfig, nodeDatum.fullName) }
      : typeInfo(this.props.viewConfig, nodeDatum.type);

    const onClick = async () => {
      if (!accessible) {
        toast.warning(LOCKED_NOTICE);
        return;
      }
      const wasExpanded = nodeDatum.children?.length > 0;
      // Clicking a relationship reads it, the way clicking an edge in the
      // Neo4j browser does. In a tree the relationship is the node between an
      // object and what it reaches, so this one gesture both opens it and
      // says what it is - there is no line to click separately.
      if (nodeDatum.isLink) {
        this.setState({
          selectedGroupId: nodeDatum.id,
          ...(this.props.wide ? { panelOpen: true } : { drawerOpen: true }),
        });
        this.readGroup(nodeDatum);
      }
      toggleNode();
      await this.addChildNode(nodeDatum);
      // Collapsing focuses the node itself; its parent stays the current path end.
      const path = findPath(this.state.data, nodeDatum.id);
      const focusId = wasExpanded ? (path[path.length - 2]?.id || nodeDatum.id) : nodeDatum.id;
      // Once the reader drives, the note about the first level's cap has had
      // its say: opening this object again brings every relationship group.
      this.setState({ focusId, firstLevelHidden: 0 }, () => this.scheduleFocus(nodeDatum.id, !wasExpanded));
    };
    // Mouse clicks must not move focus: focusing a node scrolls its clipped containers.
    const noFocusScroll = (e) => e.preventDefault();
    // Anything drawn as a button answers to both Enter and Space; Space would
    // otherwise scroll the page out from under the reader.
    const onActivate = (run) => (e) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      e.preventDefault();
      e.stopPropagation();
      run();
    };

    if (nodeDatum.isLink) {
      return (
        <g>
          <foreignObject x="0" y={-NODE_HEIGHT / 2} height={NODE_HEIGHT} width={NODE_WIDTH + 20}>
            <div className="re-node-wrap" data-node-id={nodeDatum.id}>
              <button
                type="button"
                className={`re-group re-group--${nodeDatum.direction === "in" ? "in" : "out"}${expanded ? " re-group--open" : ""}${
                  this.state.selectedGroupId === nodeDatum.id ? " re-group--chosen" : ""
                }`}
                onMouseDown={noFocusScroll}
                onClick={onClick}
                title={[groupTitle(this.props.viewConfig, nodeDatum.fullName, nodeDatum.direction), carriesNote(nodeDatum.attributes)]
                  .filter(Boolean)
                  .join(". ")}
              >
                {/* The arrow is read at a glance and sits at the end it points
                    at; the words are on the chip's own title, because a reader
                    should never have to know what an arrow means in a graph. */}
                <span className="re-group__way" aria-hidden="true">{directionMark(nodeDatum.direction)}</span>
                <span className="re-group__name">{this.getShortName(info.label)}</span>
                {/* This relationship carries attributes of its own. The mark
                    says so at a glance; what they are is on the chip's title,
                    and what each edge holds is in the panel. */}
                {nodeDatum.attributes?.length ? (
                  <InfoOutlinedIcon className="re-group__mark" aria-hidden="true" sx={{ fontSize: 14 }} />
                ) : null}
                {nodeDatum?.total ? <span className="re-group__count">{nodeDatum.total}</span> : null}
              </button>
            </div>
          </foreignObject>
        </g>
      );
    }

    return (
      <g>
        <foreignObject x="0" y={-NODE_HEIGHT / 2} height={NODE_HEIGHT} width={NODE_WIDTH + 20}>
          <div className="re-node-wrap" data-node-id={nodeDatum.id}>
            <div
              role="button"
              tabIndex={0}
              className={`re-node${selected ? " re-node--selected" : ""}${accessible ? "" : " re-node--locked"}${expanded ? " re-node--open" : ""}`}
              style={{ "--re-accent": info.colour }}
              onMouseDown={noFocusScroll}
              onClick={onClick}
              onKeyDown={onActivate(onClick)}
              onMouseOver={() => {
                clearTimeout(factSheetShowTimeout);
                factSheetShowTimeout = setTimeout(() => this.handleShow(nodeDatum), 600);
              }}
              onMouseOut={() => {
                clearTimeout(factSheetShowTimeout);
                this.handleClose();
              }}
            >
              <span className="re-node__text">
                {(nodeDatum.type || nodeDatum.distinguisher) && (
                  <span className="re-node__type">
                    <TypeIcon name={info.icon} sx={{ fontSize: 12, mr: 0.5, verticalAlign: "-2px" }} />
                    {info.label}
                    {/* What tells this one from its namesakes, beside its type
                        rather than under its name: the card has room for it
                        there without growing. */}
                    {nodeDatum.distinguisher && (
                      <span className="re-node__apart"> · {nodeDatum.distinguisher.value}</span>
                    )}
                  </span>
                )}
                <span className="re-node__name">{this.getShortName(nodeDatum.fullName) || info.label}</span>
              </span>
              {accessible ? (
                <span className="re-node__actions" onMouseDown={noFocusScroll}>
                  {nodeDatum.hasDiagram && (
                    <Tooltip title={`Open ${diagram.name} in a new tab`} placement="top" arrow>
                      <span
                        className="re-node__action re-node__action--diagram"
                        role="button"
                        tabIndex={0}
                        aria-label={`Open the ${diagram.name} of ${nodeDatum.fullName} in a new tab`}
                        onClick={(e) => {
                          e.stopPropagation();
                          this.openDiagram(nodeDatum, diagram.format);
                        }}
                        onKeyDown={onActivate(() => this.openDiagram(nodeDatum, diagram.format))}
                      >
                        <SchemaOutlinedIcon />
                      </span>
                    </Tooltip>
                  )}
                  <Tooltip title="Details" placement="top" arrow>
                    <span
                      className="re-node__action"
                      role="button"
                      tabIndex={0}
                      aria-label={`Details for ${nodeDatum.fullName}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        this.showDetails(nodeDatum);
                      }}
                      onKeyDown={onActivate(() => this.showDetails(nodeDatum))}
                    >
                      <InfoOutlinedIcon />
                    </span>
                  </Tooltip>
                </span>
              ) : (
                <Tooltip title={LOCKED_LABEL} placement="top" arrow>
                  <span className="re-node__locked">
                    <LockRoundedIcon />
                  </span>
                </Tooltip>
              )}
            </div>
          </div>
        </foreignObject>
      </g>
    );
  };

  // Opening one node, once.
  //
  // It reads the emptiness of `children`, then waits on two network calls,
  // then **appends** what came back. Two calls for the same node that overlap
  // therefore both find it empty, both fetch, and both append - which is how
  // an object came to show its relationships twice. One caller was enough to
  // be safe by accident; the panel asking to open a group it wants to point
  // into is a second.
  //
  // The id is held for as long as the opening takes, so the second caller
  // waits for the first rather than racing it, and whoever asked gets the
  // opened node either way.
  addChildNode = async (nodeDatum) => {
    if (!nodeDatum) return undefined;
    const inFlight = this.opening.get(nodeDatum.id);
    if (inFlight) return inFlight;
    const run = this.openNode(nodeDatum).finally(() => this.opening.delete(nodeDatum.id));
    this.opening.set(nodeDatum.id, run);
    return run;
  };

  openNode = async (nodeDatum) => {
    const nextData = { ...this.state.data };
    const newNodes = [];

    // Already open: collapse it.
    if (nodeDatum?.children.length > 0) {
      popNodes(nodeDatum.id, nextData);
      return this.setState({ data: nextData });
    }

    // Opening a branch closes its open siblings, so only the path being explored stays expanded.
    const path = findPath(nextData, nodeDatum.id);
    const parent = path[path.length - 2];
    (parent?.children || []).forEach((sibling) => {
      if (sibling.id !== nodeDatum.id && sibling.children?.length) sibling.children = [];
    });

    if (!nodeDatum.isLink) {
      if (this.props.user.subscription === "trial" && !nodeDatum.inTrial) {
        toast.warning("This content is not available in the trial version.");
        return;
      }
      const edges = await getEdges(nodeDatum.guid);
      for (let edge of edges) {
        // One group per relationship *and* direction: the same relationship
        // followed both ways is two different things to say about an object,
        // and a reader who can't tell them apart can't tell where an edge
        // starts from where it ends.
        const label = relationshipLabel(this.props.viewConfig, edge.relationship);
        newNodes.push({
          id: uuidv4(),
          guid: nodeDatum.guid,
          shortName: this.getShortName(label),
          fullName: edge.relationship,
          direction: edge.direction,
          from: nodeDatum.fullName,
          total: edge.total,
          // Which attributes this relationship carries at all - the names
          // only, so the chip can mark itself without a second read. What each
          // edge actually holds comes with the objects below.
          attributes: edge.attributes || [],
          children: [],
          isLink: true,
        });
      }
    } else {
      const nodes = await getNodes(nodeDatum.guid, nodeDatum.fullName, nodeDatum.direction);
      for (let node of nodes) {
        newNodes.push({
          id: uuidv4(),
          guid: node.GUID,
          shortName: this.getShortName(node.name),
          fullName: node.name,
          type: node.Type,
          // How this object was reached, carried on the object itself under
          // the names the structure map already uses - so the hover card and
          // the details panel read one vocabulary, not two.
          _relationship: nodeDatum.fullName,
          _relationshipLabel: relationshipLabel(this.props.viewConfig, nodeDatum.fullName),
          _direction: nodeDatum.direction,
          _from: nodeDatum.from,
          _relationshipAttributes: node._relationshipAttributes || [],
          description: node.Description,
          children: [],
          isLink: false,
          inTrial: node.inTrial === true,
          url: node?.URL,
          item: node,
          diagramOf: node._diagramOf || null,
        });
      }
    }

    // No way back into the loop: an object already on the path back to the
    // root (the one this branch was expanded from, or any ancestor further
    // up) is not offered again as a child - the breadcrumb on the left is
    // the way back. Relationship-group nodes reuse their parent's GUID on
    // purpose (it drives the next fetch), so this only ever filters real
    // objects, fetched just above.
    const childNodes = nodeDatum.isLink ? withoutWayBack(newNodes, path.map((n) => n.guid)) : newNodes;

    // Mark objects that have a diagram (any format) so they can be opened directly.
    const objectNodes = childNodes.filter((n) => !n.isLink);
    if (objectNodes.length) {
      const availability = await getDiagramAvailability(objectNodes.map((n) => n.guid));
      objectNodes.forEach((n) => {
        // A diagram node is itself a diagram of the object it belongs to.
        n.hasDiagram = Boolean(availability[n.guid]) || Boolean(n.diagramOf);
        n.diagramFormats = availability[n.guid] || (n.diagramFormat ? [n.diagramFormat] : []);
      });
    }

    // Where several of these are shown under one name, mark each with whatever
    // actually tells it apart - computed once, here, over the siblings that
    // arrived together. Five objects with the same title and no way to tell
    // them apart read as five copies of one.
    const apart = distinguishers(childNodes.filter((n) => !n.isLink).map(asGroupItem));
    childNodes.forEach((n) => {
      n.distinguisher = apart.get(n.guid) || null;
    });

    appendNodes(nodeDatum.id, nextData, childNodes);
    this.setState({ data: nextData });
  };

  getShortName(name) {
    return shortName(name, setting(this.props.viewConfig, "explorer.nodeLabelLength"));
  }

  // typesInTree walks the whole tree; cache the result per `data` reference
  // so re-renders that don't change the tree (hover, zoom, panel toggles,
  // selection) don't re-walk it just to redraw the legend.
  getTreeTypes(data) {
    if (this._treeTypesSource !== data) {
      this._treeTypesSource = data;
      this._treeTypes = [...typesInTree(data)];
    }
    return this._treeTypes;
  }

  zoomBy = (delta) =>
    this.setState(({ zoom }) => ({ zoom: Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, +(zoom + delta).toFixed(2))) }));

  toggleFullscreen = () => {
    if (this.state.isFullScreen) exitFullscreen();
    else requestFullscreen(document.getElementById("graph"));
  };

  render() {
    const { wide } = this.props;
    const { data, selected, details, panelOpen, drawerOpen, selectedGroupId } = this.state;
    const showPanel = wide && panelOpen;

    // The relationship the reader last clicked, read out of the tree rather
    // than kept beside it: what it reaches is only known once it has been
    // opened, and the tree is where that arrives.
    const groupPath = selectedGroupId && data ? findPath(data, selectedGroupId) : [];
    const group = groupPath[groupPath.length - 1] || null;
    // What the canvas is holding, where it is holding it; else what the panel
    // read for itself. `null` is "still reading", which is not the same answer
    // as "reaches nothing".
    const groupItems = group?.children?.length
      ? group.children.map(asGroupItem)
      : this.state.groupItems?.id === selectedGroupId
      ? this.state.groupItems.items
      : null;

    const detailsPanel = group ? (
      <RelationshipDetails
        key={group.id}
        relationship={group.fullName}
        direction={group.direction}
        here={group.from}
        total={group.total}
        carries={group.attributes}
        items={groupItems}
        loading={groupItems === null}
        onOpen={(item) => this.revealObject(group, item)}
        onClose={wide ? () => this.setState({ panelOpen: false }) : () => this.setState({ drawerOpen: false })}
      />
    ) : (
      <DetailsPanel
        key={selected?.GUID || "none"}
        selected={selected}
        details={details}
        path={this.props.path}
        emptyText="Hover an object and choose Details, or use the info button on a node, to see its attributes, occurrences and relationships."
        onClose={wide ? () => this.setState({ panelOpen: false }) : () => this.setState({ drawerOpen: false })}
        onOpenOccurrence={this.openObject}
        onOpenRelationship={this.openObject}
        onFindInSearch={this.findInSearch}
        onOpenDiagram={(item, format) => this.openDiagram({ guid: item.GUID }, format)}
      />
    );

    return (
      <ThemeProvider theme={businessFlowsTheme}>
        <Box
          className="bfx"
          sx={{
            flex: 1,
            height: "100%",
            minHeight: 0,
            boxSizing: "border-box",
            bgcolor: "background.default",
            p: { xs: 1, sm: 1.5, md: 2 },
            display: "grid",
            gap: 1.5,
            gridTemplateColumns: showPanel ? "minmax(0, 1fr) 360px" : "minmax(0, 1fr)",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, minHeight: 0, minWidth: 0 }}>
            {/* Toolbar */}
            <Paper
              elevation={0}
              className="noselect"
              sx={{
                px: { xs: 1, md: 1.5 },
                py: 0.75,
                borderRadius: radius.surface,
                border: `1px solid ${brand.line}`,
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: { xs: "wrap", md: "nowrap" },
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: radius.control,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: brand.ink,
                  color: brand.signal,
                  flexShrink: 0,
                }}
              >
                <HubRoundedIcon fontSize="small" />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1, order: { xs: 3, md: 0 }, flexBasis: { xs: "100%", md: "auto" } }}>
                {(() => {
                  const path = findPath(data, this.state.focusId);
                  if (path.length < 2) {
                    return (
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", lineHeight: 1.2 }}>
                        Relationship Explorer
                      </Typography>
                    );
                  }
                  return (
                    <Breadcrumbs
                      aria-label="Drill path"
                      maxItems={wide ? 6 : 3}
                      itemsBeforeCollapse={1}
                      itemsAfterCollapse={wide ? 4 : 2}
                      separator={<NavigateNextRoundedIcon sx={{ fontSize: 14 }} />}
                      sx={{ "& .MuiBreadcrumbs-ol": { flexWrap: "nowrap" }, "& .MuiBreadcrumbs-li": { minWidth: 0 }, lineHeight: 1.2 }}
                    >
                      {path.map((node, i) => (
                        <ButtonBase
                          key={node.id}
                          onClick={() => this.drillOutTo(i)}
                          title={node.isLink ? relationshipLabel(this.props.viewConfig, node.fullName) : node.fullName}
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: i === path.length - 1 ? 700 : 500,
                            color: i === path.length - 1 ? brand.ink : "text.secondary",
                            fontStyle: node.isLink ? "italic" : "normal",
                            borderRadius: 1,
                            px: 0.25,
                            maxWidth: 160,
                            display: "block",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            "&:hover": { color: brand.ink, textDecoration: "underline" },
                          }}
                        >
                          {node.isLink ? relationshipLabel(this.props.viewConfig, node.fullName) : node.fullName}
                        </ButtonBase>
                      ))}
                    </Breadcrumbs>
                  );
                })()}
                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                  <Typography
                    component="h1"
                    title={data?.fullName}
                    sx={{ fontWeight: 700, fontSize: "1rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                  >
                    {data?.fullName || "Relationship map"}
                  </Typography>
                  {data?.type && (
                    <Chip
                      size="small"
                      label={typeInfo(this.props.viewConfig, data.type).label}
                      sx={{ bgcolor: brand.mark, color: brand.ink, fontWeight: 600, flexShrink: 0, display: { xs: "none", sm: "inline-flex" } }}
                    />
                  )}
                </Stack>
              </Box>

              <Stack direction="row" alignItems="center" spacing={0.25} sx={{ flexShrink: 0, ml: "auto" }}>
                <ToolButton title="Zoom out" onClick={() => this.zoomBy(-0.1)} disabled={this.state.zoom <= MIN_ZOOM}>
                  <ZoomOutRoundedIcon fontSize="small" />
                </ToolButton>
                <ToolButton title="Zoom in" onClick={() => this.zoomBy(0.1)} disabled={this.state.zoom >= MAX_ZOOM}>
                  <ZoomInRoundedIcon fontSize="small" />
                </ToolButton>
                <ToolButton title="Fit to screen" onClick={() => this.fitView()}>
                  <FitScreenRoundedIcon fontSize="small" />
                </ToolButton>
                <ToolButton title={this.state.isFullScreen ? "Exit full screen" : "Full screen"} onClick={this.toggleFullscreen}>
                  {this.state.isFullScreen ? <FullscreenExitRoundedIcon fontSize="small" /> : <FullscreenRoundedIcon fontSize="small" />}
                </ToolButton>
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                <ToolButton title="Start over" onClick={this.startOver}>
                  <RestartAltRoundedIcon fontSize="small" />
                </ToolButton>
                <ToolButton
                  title={showPanel ? "Hide properties" : "Show properties"}
                  onClick={() => (wide ? this.setState({ panelOpen: !panelOpen }) : this.setState({ drawerOpen: true }))}
                  sx={{ bgcolor: showPanel ? brand.canvas : "transparent" }}
                >
                  <TuneRoundedIcon fontSize="small" />
                </ToolButton>
              </Stack>
            </Paper>

            {/* Canvas */}
            <Paper
              elevation={0}
              id="graph"
              ref={(tc) => (this.treeContainer = tc)}
              className="re-canvas"
              sx={{ flex: 1, minHeight: { xs: 380, md: 0 }, borderRadius: radius.surface, border: `1px solid ${brand.line}`, overflow: "hidden", overflowClipMargin: 0, position: "relative" }}
            >
              {this.state.databaseError ? (
                // The database can't be reached: its own message, not a canvas
                // that looks as though this object has nothing around it.
                <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ height: "100%", p: 3 }}>
                  <Alert severity="warning" role="alert" sx={{ maxWidth: 520 }}>
                    {this.state.databaseError}
                  </Alert>
                  <Button variant="outlined" size="small" onClick={() => window.location.reload()}>
                    Try again
                  </Button>
                </Stack>
              ) : !data ? (
                // Nothing is drawn until the object in the URL is known, so no
                // other node ever appears first. The wait is shown as the shape
                // of the tree about to arrive - a card and the relationships
                // beside it - rather than a spinner on an empty canvas.
                <Box
                  role="status"
                  aria-label="Loading the object"
                  sx={{ height: "100%", display: "flex", alignItems: "center", gap: { xs: 3, md: 7 }, px: { xs: 3, md: 6 } }}
                >
                  <Skeleton variant="rounded" width={240} height={48} sx={{ borderRadius: radius.control, flexShrink: 0 }} />
                  <Stack spacing={1.5} sx={{ flexShrink: 0 }}>
                    {[0, 1, 2].map((i) => (
                      <Skeleton key={i} variant="rounded" width={200} height={36} sx={{ borderRadius: 999 }} />
                    ))}
                  </Stack>
                </Box>
              ) : (
              <Tree
                data={data}
                renderCustomNodeElement={(rd3tProps) => this.renderNodeWithCustomEvents({ ...rd3tProps })}
                translate={this.state.translate}
                /* No `dimensions`: with it the library runs its own centring animation on every
                   click (using the pre-expansion layout), which fights focusOn. */
                zoom={this.state.zoom}
                orientation="horizontal"
                nodeSize={this.state.nodeSize}
                scaleExtent={{ min: MIN_ZOOM, max: MAX_ZOOM }}
                separation={{ siblings: 1, nonSiblings: 1.25 }}
                enableLegacyTransitions={false}
                transitionDuration={TRANSITION_MS}
                pathClassFunc={this.getDynamicPathClass}
                pathFunc={this.straightPathFunc}
              />
              )}
              {this.state.firstLevelHidden > 0 && (
                // The first level opened with as much as reads well. Nothing
                // is out of reach: the object opens them all on the next click.
                <Paper
                  elevation={0}
                  role="status"
                  sx={{
                    position: "absolute",
                    top: 12,
                    right: 12,
                    maxWidth: "min(320px, calc(100% - 24px))",
                    px: 1.5,
                    py: 0.75,
                    borderRadius: radius.card,
                    border: `1px solid ${brand.line}`,
                    bgcolor: "rgba(255,255,255,0.96)",
                    pointerEvents: "none",
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {this.state.firstLevelHidden === 1
                      ? "One more relationship group. Open this object again to see them all."
                      : `${this.state.firstLevelHidden} more relationship groups. Open this object again to see them all.`}
                  </Typography>
                </Paper>
              )}
              <ExplorerLegend types={this.getTreeTypes(data)} />
            </Paper>
          </Box>

          {showPanel && (
            <Paper elevation={0} sx={{ borderRadius: radius.surface, border: `1px solid ${brand.line}`, overflow: "hidden", minHeight: 0 }}>
              {detailsPanel}
            </Paper>
          )}
        </Box>

        {!wide && (
          <Drawer
            anchor="right"
            open={drawerOpen}
            onClose={() => this.setState({ drawerOpen: false })}
            PaperProps={{ className: "bfx", sx: { width: "min(400px, 92vw)" } }}
          >
            {detailsPanel}
          </Drawer>
        )}

        <HoverComponent
          {...this.props}
          show={this.state.show && !this.state.nodeDatum.isLink}
          nodeDatum={this.state?.nodeDatum}
          zoom={this.state.zoom}
          onShowDetails={this.showDetails}
          onOpenDiagram={this.openDiagram}
          onFindInSearch={(node) => this.findInSearch({ GUID: node.guid, name: node.fullName, Type: node.type })}
        />
        <ToastContainer position="top-center" autoClose={3000} />
      </ThemeProvider>
    );
  }
}

const ExplorerWithRouter = withRouter(CustomD3Tree);

export default function RelationshipExplorerView(props) {
  const wide = useMediaQuery(businessFlowsTheme.breakpoints.up("lg"));
  // Labels, colours, icons and lengths update on the fly when an admin changes them.
  const viewConfig = useViewConfig();
  return <ExplorerWithRouter {...props} wide={wide} viewConfig={viewConfig} />;
}
